import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/api';
import ClientLayout from '../components/ClientLayout';

const STATUS_STYLES = {
    'SOLICITADO': { bg: '#FEF3C7', color: '#D97706', label: '⏳ Solicitado' },
    'CONFIRMADO': { bg: '#DBEAFE', color: '#2563EB', label: '✅ Confirmado' },
    'EN_RUTA': { bg: '#E0E7FF', color: '#4F46E5', label: '🚚 En Ruta' },
    'ENTREGADO': { bg: '#D1FAE5', color: '#059669', label: '🏁 Entregado' },
    'CANCELADO': { bg: '#FEE2E2', color: '#DC2626', label: '❌ Cancelado' }
};

const COLORS = {
    green: '#2E5B2E', pink: '#C19A6B', cream: '#F9F9F5',
    dark: '#333333', white: '#FFFFFF', red: '#DC2626'
};

function DetallePedido() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [pedido, setPedido] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) return navigate('/login');

        api.get(`/pedidos/${id}`, { headers: { Authorization: `Bearer ${token}` } })
            .then(res => {
                setPedido(res.data);
                setLoading(false);
            })
            .catch(err => {
                setError('No se pudo cargar la información.');
                setLoading(false);
            });
    }, [id, navigate]);

    // --- FUNCIÓN PARA DESCARGAR / IMPRIMIR COMPROBANTE ---
    const handleDownloadPDF = () => {
        window.print(); // Esto abrirá el diálogo de impresión con el formato optimizado
    };

    const renderStatus = (estado) => {
        const style = STATUS_STYLES[estado] || STATUS_STYLES['SOLICITADO'];
        return (
            <span style={{ 
                background: style.bg, color: style.color, 
                padding: '6px 14px', borderRadius: 20, fontSize: 14, fontWeight: 'bold' 
            }}>
                {style.label}
            </span>
        );
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(amount || 0);
    };

    const labelStyle = { fontWeight: 'bold', color: '#666', marginBottom: 5, fontSize: 13, textTransform: 'uppercase' };
    const valueStyle = { color: COLORS.dark, marginBottom: 20, fontSize: 16, fontWeight: '500' };

    if (loading) return <ClientLayout><div style={{textAlign:'center', marginTop:50}}>Cargando...</div></ClientLayout>;
    if (error) return <ClientLayout><div style={{textAlign:'center', color: COLORS.red, marginTop:50}}>{error}</div></ClientLayout>;
    if (!pedido) return null;

    return (
        <ClientLayout title={`Pedido #${pedido.folioRecibo || id.slice(0, 8)}`}>
            {/* ESTILO PARA OCULTAR BOTONES AL IMPRIMIR EL PDF */}
            <style>
                {`
                    @media print {
                        nav, button, .no-print { display: none !important; }
                        .card-print { box-shadow: none !important; border: 1px solid #eee !important; margin: 0 !important; width: 100% !important; }
                        body { background: white !important; }
                    }
                `}
            </style>

            <div style={{ maxWidth: 800, margin: '0 auto' }} className="card-print">
                <div style={{ background: COLORS.white, borderRadius: 16, padding: 40, boxShadow: '0 8px 25px rgba(0,0,0,0.08)', position: 'relative' }}>
                    
                    {/* CABECERA */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${COLORS.cream}`, paddingBottom: 20, marginBottom: 30, flexWrap: 'wrap', gap: 15 }}>
                        <div>
                            <h2 style={{ color: COLORS.green, margin: 0, fontSize: 28 }}>Xavana Café</h2>
                            <span style={{ fontSize: 14, color: COLORS.pink, fontWeight: 'bold', fontFamily: 'monospace' }}>
                                FOLIO: {pedido.folioRecibo || 'PENDIENTE'}
                            </span>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                            {renderStatus(pedido.estado)}
                            <div style={{ marginTop: 8, fontSize: 14, fontWeight: 'bold', color: pedido.pagado ? COLORS.green : COLORS.red }}>
                                {pedido.pagado ? '💳 PAGADO' : '⏳ PENDIENTE DE PAGO'}
                            </div>
                        </div>
                    </div>

                    {/* SECCIÓN DE ENTREGA (SOLO SI ESTÁ ENTREGADO) */}
                    {pedido.estado === 'ENTREGADO' && (
                        <div style={{ background: '#f0fff4', border: `1px solid ${COLORS.green}40`, padding: '15px 20px', borderRadius: 12, marginBottom: 30, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <div style={labelStyle}>Recibido por</div>
                                <div style={{ fontSize: 18, fontWeight: 'bold', color: COLORS.green }}>👤 {pedido.recibidoPor || 'Confirmado'}</div>
                            </div>
                            <button 
                                onClick={handleDownloadPDF}
                                className="no-print"
                                style={{ background: COLORS.green, color: 'white', border: 'none', padding: '8px 15px', borderRadius: 8, cursor: 'pointer', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 8 }}
                            >
                                📄 Descargar Comprobante
                            </button>
                        </div>
                    )}
                    {/* GRID DE INFORMACIÓN DE FECHAS */}
                    <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
                        gap: 20,
                        marginBottom: 30,
                        borderBottom: `1px solid ${COLORS.cream}`,
                        paddingBottom: 20 
                    }}>
                        {/* 1. Fecha de Solicitud */}
                        <div>
                            <div style={labelStyle}>Solicitud</div>
                            <div style={valueStyle}>
                                {new Date(pedido.createdAt).toLocaleDateString('es-MX')}
                            </div>
                        </div>

                        {/* 2. Fecha Programada */}
                        <div>
                            <div style={labelStyle}>Programada</div>
                            <div style={valueStyle}>
                                {pedido.fechaEntrega 
                                    ? new Date(pedido.fechaEntrega).toLocaleDateString('es-MX') 
                                    : 'Pendiente'}
                            </div>
                        </div>

                        {/* 3. Fecha Real (Extraída del Folio: X-26-2101-XXX) */}
                        <div>
                            <div style={labelStyle}>Entrega Real</div>
                            <div style={{
                                ...valueStyle, 
                                color: pedido.folioRecibo ? COLORS.green : '#888',
                                fontWeight: pedido.folioRecibo ? 'bold' : '500'
                            }}>
                                {pedido.folioRecibo ? (
                                    // Lógica para formatear la fecha del folio X-26-DDMM-XXX
                                    (() => {
                                        const partes = pedido.folioRecibo.split('-');
                                        const diaMes = partes[2]; // Esto es "2101"
                                        const dia = diaMes.substring(0, 2);
                                        const mes = diaMes.substring(2, 4);
                                        const anio = `20${partes[1]}`; // Esto es "2026"
                                        return `${dia}/${mes}/${anio}`;
                                    })()
                                ) : (
                                    'Pendiente'
                                )}
                            </div>
                        </div>
                    </div>

                    {/* INFORMACIÓN DE SUCURSAL (Movida debajo de las fechas para mejor lectura) */}
                    <div style={{ marginBottom: 30 }}>
                        <div style={labelStyle}>Lugar de Entrega</div>
                        <div style={valueStyle}>
                            <span style={{fontWeight: 'bold'}}>{pedido.sucursal?.nombre || 'Sin sucursal'}</span>
                            <div style={{fontSize: 13, color: '#666', marginTop: 4}}>{pedido.sucursal?.direccion}</div>
                        </div>
                    </div>

                    {/* DETALLE DEL PRODUCTO */}
                    <div style={{ background: COLORS.cream, borderRadius: 12, padding: 25, marginTop: 10, border: `1px solid ${COLORS.pink}30` }}>
                        <h3 style={{ color: COLORS.pink, marginTop: 0, marginBottom: 20 }}>📦 {pedido.tipoProducto?.replace(/_/g, ' ')}</h3>
                        
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 15, borderBottom: '1px dashed #ccc', paddingBottom: 15 }}>
                            <div style={{ textAlign: 'center', flex: 1 }}>
                                <div style={{ fontSize: 24, fontWeight: 'bold', color: COLORS.green }}>{pedido.cantidadGrano} <span style={{fontSize:14}}>Kg</span></div>
                                <div style={{ fontSize: 12, color: '#666' }}>EN GRANO</div>
                            </div>
                            <div style={{ width: 1, background: '#ccc' }}></div>
                            <div style={{ textAlign: 'center', flex: 1 }}>
                                <div style={{ fontSize: 24, fontWeight: 'bold', color: COLORS.green }}>{pedido.cantidadMolido} <span style={{fontSize:14}}>Kg</span></div>
                                <div style={{ fontSize: 12, color: '#666' }}>MOLIDO</div>
                            </div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <div style={labelStyle}>TOTAL KILOS</div>
                                <div style={{ fontSize: 18, fontWeight: 'bold' }}>
                                    {(Number(pedido.cantidadGrano) + Number(pedido.cantidadMolido)).toFixed(1)} Kg
                                </div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                                <div style={labelStyle}>MONTO TOTAL</div>
                                <div style={{ fontSize: 24, fontWeight: 'bold', color: COLORS.pink }}>
                                    {formatCurrency(pedido.montoTotal)}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* BOTONES DE NAVEGACIÓN */}
                    <div className="no-print" style={{ marginTop: 40, textAlign: 'center', display: 'flex', justifyContent: 'center', gap: 15 }}>
                        <button 
                            onClick={() => navigate('/pedidos-anteriores')} 
                            style={{ background: 'transparent', border: `2px solid ${COLORS.green}`, color: COLORS.green, padding: '10px 25px', borderRadius: 8, fontWeight: 'bold', cursor: 'pointer' }}
                        >
                            ← Volver al Historial
                        </button>
                    </div>

                </div>
            </div>
        </ClientLayout>
    );
}

export default DetallePedido;